import type { Dispatch, SetStateAction } from "react";
import moment from "moment";
import { get, isNull } from "lodash";
import { isHttpSuccess, orgId, getPrimaryUnitName, getPrimaryUnit, getPrimaryUnitKey } from "utils/functions";
import { convertIdToUserInfoArray } from "utils/gatewayFunctions";
import { getCalendarFormat } from "utils/datetime";
import {
    AlertPeriod,
    BinarySensorsCLSList,
    Operator,
    SensorAlertMode,
    SensorDataType,
    SensorDefaultMessage,
    SensorThreshold,
    HttpStatus,
} from "constant";
import type { AlertConfig, MemberDto } from "types";
import { getAllAlertNotification, fetchDetailDevice } from "service/gatewayService";
import OrganisationService from "service/organisationService";
import { DEFAULT_VALUES } from "./constants";

const { FORBIDDEN } = HttpStatus;
const { ALERT_ONCE, ALERT_CONTINUOUSLY } = SensorAlertMode;
const { OUT_OF_RANGE, WITHIN_RANGE, LESS_THAN, GREATER_THAN, EQUAL_TO } = Operator;

/** Setters and inputs required to run the initial SensorDetails mount / load effect (extracted for cognitive complexity). */
export type SensorDetailsMountContext = {
    validateAndRedirect: () => boolean;
    locationState: any;
    id: string;
    sensorId: string;
    reformattedNumberString: (value: number | string) => string;
    fetchSensorMembership: (sensorUID: string, gatewayId: string, sensorSAID?: number | null) => void | Promise<void>;
    setIsPageLoading: Dispatch<SetStateAction<boolean>>;
    setSensor: Dispatch<SetStateAction<any>>;
    setSensorName: Dispatch<SetStateAction<string>>;
    setSensorMode: Dispatch<SetStateAction<SensorThreshold>>;
    setBinaryValue: Dispatch<SetStateAction<string>>;
    setSingleThresholdValue: Dispatch<SetStateAction<string>>;
    setDualMinValue: Dispatch<SetStateAction<string>>;
    setDualMaxValue: Dispatch<SetStateAction<string>>;
    setSingleActivateCondition: Dispatch<SetStateAction<string>>;
    setDualActivateCondition: Dispatch<SetStateAction<string>>;
    setAlertMode: Dispatch<SetStateAction<SensorAlertMode>>;
    setAlertPeriod: Dispatch<SetStateAction<number>>;
    setActivateHeaderContent: Dispatch<SetStateAction<string>>;
    setActivateMessageContent: Dispatch<SetStateAction<string>>;
    setDeactivateHeaderContent: Dispatch<SetStateAction<string>>;
    setDeactivateMessageContent: Dispatch<SetStateAction<string>>;
    setActivationCheckboxState: Dispatch<SetStateAction<boolean>>;
    setDeactivationCheckboxState: Dispatch<SetStateAction<boolean>>;
    setInitialMemberList: Dispatch<SetStateAction<MemberDto[]>>;
    setAlertUUID: Dispatch<SetStateAction<string>>;
    setCurrEnabledStatus: Dispatch<SetStateAction<boolean>>;
    setAlertConfig: Dispatch<SetStateAction<AlertConfig>>;
    setSensorJsonInfo: Dispatch<SetStateAction<any>>;
    setOpMode: Dispatch<SetStateAction<number>>;
};

function sensorMountConfigureThresholdValues(ctx: SensorDetailsMountContext, data: any): void {
    const ops = Array.isArray(data?.ops) ? data.ops : [];
    const vars_ = Array.isArray(data?.vars_) ? data.vars_ : [];

    if (ops[0] === EQUAL_TO) {
        if (vars_.length > 1) {
            ctx.setSensorMode(SensorThreshold.NO_THRESHOLD);
            ctx.setBinaryValue(vars_[1]);
        }
        return;
    }

    if (vars_.length === 2) {
        ctx.setSensorMode(SensorThreshold.SINGLE_THRESHOLD);
        ctx.setSingleThresholdValue(ctx.reformattedNumberString(vars_[1]));
        ctx.setDualMinValue(ctx.reformattedNumberString("0"));
        ctx.setDualMaxValue(ctx.reformattedNumberString("0"));
        return;
    }

    if (vars_.length >= 4) {
        ctx.setSensorMode(SensorThreshold.DUAL_THRESHOLD);
        ctx.setDualMinValue(ctx.reformattedNumberString(vars_[1]));
        ctx.setDualMaxValue(ctx.reformattedNumberString(vars_[3]));
        ctx.setSingleThresholdValue(ctx.reformattedNumberString("0"));
        return;
    }

    ctx.setSensorMode(SensorThreshold.SINGLE_THRESHOLD);
    ctx.setSingleThresholdValue(ctx.reformattedNumberString("0"));
    ctx.setDualMinValue(ctx.reformattedNumberString("0"));
    ctx.setDualMaxValue(ctx.reformattedNumberString("0"));
}

function sensorMountConfigureActivationConditions(ctx: SensorDetailsMountContext, ops: any[]): void {
    if (ops.length === 1) {
        if (ops[0] === GREATER_THAN) {
            ctx.setSingleActivateCondition(GREATER_THAN);
        } else {
            ctx.setSingleActivateCondition(LESS_THAN);
        }
    }

    if (ops.length === 3) {
        if (ops[0] === Operator.GREATER_THAN && ops[2] === Operator.LESS_THAN) {
            ctx.setDualActivateCondition(WITHIN_RANGE.toString());
        } else {
            ctx.setDualActivateCondition(OUT_OF_RANGE.toString());
        }
    }
}

function sensorMountConfigureAlertRepeat(ctx: SensorDetailsMountContext, repeat: number): void {
    if (repeat === AlertPeriod.ALERT_ONCE_MAX) {
        ctx.setAlertMode(ALERT_ONCE);
        return;
    }

    ctx.setAlertMode(ALERT_CONTINUOUSLY);
    ctx.setAlertPeriod(repeat);
}

function sensorMountResolveNoticeField(
    value: string | null | undefined,
    defaultValue: string
): string {
    if (value == null || String(value).trim() === "") {
        return defaultValue;
    }
    return value;
}

function sensorMountConfigureNoticeContent(ctx: SensorDetailsMountContext, notice: any): void {
    const {
        subject,
        text,
        enabled: [activationEnabled = false, deactivationEnabled = false] = [],
    } = notice || {};

    if (subject) {
        if (!isNull(subject[0])) {
            ctx.setActivateHeaderContent(
                sensorMountResolveNoticeField(subject[0], SensorDefaultMessage.activationHeader)
            );
            ctx.setActivateMessageContent(
                sensorMountResolveNoticeField(text?.[0], SensorDefaultMessage.activationMessage)
            );
        }
        if (!isNull(subject[1])) {
            ctx.setDeactivateHeaderContent(
                sensorMountResolveNoticeField(subject[1], SensorDefaultMessage.deactivationHeader)
            );
            ctx.setDeactivateMessageContent(
                sensorMountResolveNoticeField(text?.[1], SensorDefaultMessage.deactivationMessage)
            );
        }
    }

    ctx.setActivationCheckboxState(activationEnabled);
    ctx.setDeactivationCheckboxState(deactivationEnabled);
}

async function sensorMountFetchAlertMembers(ctx: SensorDetailsMountContext): Promise<MemberDto[]> {
    const { status: membersStatus, data: membersData = [] } = await OrganisationService.getOrgMembers(orgId());
    if (!isHttpSuccess(membersStatus)) {
        return [];
    }

    const members = membersData
        .toCamelCase<MemberDto>()
        .filter((acc: MemberDto) => acc.status !== "Pending" && acc.status !== "Declined");

    ctx.setInitialMemberList(members);
    return members;
}

function sensorMountBuildAlertConfig(
    enabled_push: boolean,
    recipients_push: string[] | undefined,
    members: MemberDto[],
): AlertConfig {
    return {
        Email: {
            isEnabled: false,
            recipientMode: "Email",
            recipients: [],
        },
        SMS: {
            isEnabled: false,
            recipientMode: "SMS",
            recipients: [],
        },
        Notification: {
            isEnabled: enabled_push,
            recipientMode: "Notification",
            recipients: convertIdToUserInfoArray(members, recipients_push || []),
        },
    };
}

async function sensorMountApplyExistingAlertConfiguration(ctx: SensorDetailsMountContext, data: any): Promise<void> {
    ctx.setAlertUUID(data.uuid);
    ctx.setCurrEnabledStatus(data.enabled);

    sensorMountConfigureThresholdValues(ctx, data);
    sensorMountConfigureActivationConditions(ctx, data.ops || []);
    sensorMountConfigureAlertRepeat(ctx, data.repeat);
    sensorMountConfigureNoticeContent(ctx, data.notice);

    const { enabled_push, recipients_push } = data.notice || {};

    const members = await sensorMountFetchAlertMembers(ctx);
    ctx.setAlertConfig(sensorMountBuildAlertConfig(enabled_push, recipients_push, members));
}

async function sensorMountLoadSensorAlertConfiguration(
    ctx: SensorDetailsMountContext,
    gatewayId: string,
    ldsuUuid_: string,
    sensorSAID_: number,
    onNoAlerts: () => void,
): Promise<void> {
    const listRes: any = await getAllAlertNotification(gatewayId, ldsuUuid_, sensorSAID_.toString());

    if (!isHttpSuccess(listRes.status)) {
        return;
    }

    const list = listRes.data;
    if (!Array.isArray(list) || !list.length) {
        onNoAlerts();
        return;
    }

    await sensorMountApplyExistingAlertConfiguration(ctx, list[0]);
}

function sensorMountIsBinarySensorDefault(
    format?: string | null,
    cls?: string | null
): boolean {
    if (format === SensorDataType.Boolean) {
        return true;
    }
    return Boolean(cls && BinarySensorsCLSList.includes(cls));
}

function sensorMountSetDefaultAlertMode(
    ctx: SensorDetailsMountContext,
    format?: string | null,
    cls?: string | null
): void {
    ctx.setSensorMode(
        sensorMountIsBinarySensorDefault(format, cls)
            ? SensorThreshold.NO_THRESHOLD
            : SensorThreshold.SINGLE_THRESHOLD
    );
}

async function sensorMountFetchSensorConfigurationFromNav(
    ctx: SensorDetailsMountContext,
    sensorDetails: any,
    ldsuUuid: string,
    sensorSAID: number,
): Promise<void> {
    if (!ctx.id || !ldsuUuid) {
        return;
    }

    try {
        const deviceRes = await fetchDetailDevice({
            gateway_id: ctx.id,
            device_id: ldsuUuid,
            said: sensorSAID.toString(),
        });

        if (get(deviceRes, "status") === FORBIDDEN) {
            return;
        }

        ctx.setSensorJsonInfo(deviceRes.deviceInfo);

        if (deviceRes.MODE !== undefined) {
            ctx.setOpMode(Number(deviceRes.MODE));
        }

        const extractedFormat = deviceRes.APP
            ? getPrimaryUnitName(deviceRes.APP[Number(deviceRes.SAID)])?.FORMAT
            : deviceRes.deviceInfo?.FORMAT;

        if (extractedFormat) {
            ctx.setSensor((prev: any) => (prev ? { ...prev, format: extractedFormat } : prev));
        }

        await sensorMountLoadSensorAlertConfiguration(
            ctx,
            ctx.id,
            ldsuUuid,
            sensorSAID,
            () =>
                sensorMountSetDefaultAlertMode(
                    ctx,
                    extractedFormat ?? sensorDetails.format,
                    deviceRes.deviceInfo?.CLS ?? sensorDetails.cls
                ),
        );
    } catch {
        // Error fetching sensor configuration - non-critical, continue with defaults
    }
}

function sensorMountParseSensorIdFromString(sensorIdStr: string): { ldsuUuid: string; sensorSAID: number } | null {
    const parts = sensorIdStr.split("-");
    const saidPart = parts.at(-1) ?? "0";
    const sensorSAID = Number.parseInt(saidPart, 10) || 0;
    const ldsuUuid = parts.slice(0, -1).join("-");
    return ldsuUuid ? { ldsuUuid, sensorSAID } : null;
}

function sensorMountValidateDeviceResponseData(deviceRes: any): boolean {
    return !(get(deviceRes, "status") === FORBIDDEN || (!deviceRes.deviceInfo && !deviceRes.infoJson));
}

function sensorMountExtractDeviceInfoFromResponse(deviceRes: any, sensorSAID: number) {
    const deviceInfo =
        deviceRes.deviceInfo ||
        deviceRes.infoJson?.SNS?.find((s: any) => String(s.SAID) === String(sensorSAID)) ||
        deviceRes.infoJson?.SNS?.[0];
    const primaryUnitKey = deviceRes.APP ? getPrimaryUnitKey(deviceRes.APP[Number(deviceRes.SAID)]) : null;
    return { deviceInfo, primaryUnitKey };
}

function sensorMountExtractSensorMetadataFromResponse(deviceRes: any, sensorSAID: number, sensorIdStr: string) {
    return {
        sensorName:
            deviceRes.device_name ||
            get(deviceRes, "sensor_names", {})[sensorSAID.toString()] ||
            sensorIdStr ||
            "Unknown Sensor",
        sensorType: deviceRes.type || "Unknown Type",
        ldsuName: deviceRes.device_name || deviceRes.name || "LDSU Device",
    };
}

function sensorMountExtractReadingsFromResponse(deviceRes: any) {
    return deviceRes.readings
        ? {
              value: deviceRes.readings.value ?? 0,
              lowest: deviceRes.readings.lowest ?? 0,
              highest: deviceRes.readings.highest ?? 0,
              timestamp: deviceRes.readings.timestamp || Date.now(),
          }
        : {
              value: 0,
              lowest: 0,
              highest: 0,
              timestamp: Date.now(),
          };
}

function sensorMountBuildSensorObjectFromData(
    deviceRes: any,
    deviceInfo: any,
    sensorData: {
        ldsuUuid: string;
        sensorSAID: number;
        sensorName: string;
        sensorType: string;
        ldsuName: string;
        readings: any;
        primaryUnitKey: string | null;
    },
) {
    const { ldsuUuid, sensorSAID, sensorName, sensorType, ldsuName, readings, primaryUnitKey } = sensorData;
    return {
        name: sensorName,
        type: sensorType,
        sensor_type: sensorType,
        currentValue: readings.value || 0,
        minValue: readings.lowest || 0,
        maxValue: readings.highest || 0,
        unit: deviceRes.APP ? getPrimaryUnit(deviceRes.APP[Number(deviceRes.SAID)]) : deviceInfo?.UNIT || "",
        accuracy: deviceInfo?.ACCURACY || 2,
        format: deviceRes.APP
            ? getPrimaryUnitName(deviceRes.APP[Number(deviceRes.SAID)])?.FORMAT
            : deviceInfo?.FORMAT,
        lastActive: readings.timestamp
            ? moment(readings.timestamp, "X").calendar(getCalendarFormat())
            : "-",
        ldsuName: ldsuName,
        ldsuUuid: ldsuUuid,
        SAID: sensorSAID,
        hubName: deviceRes.gateway_name || "-",
        hubPort: deviceRes.port || DEFAULT_VALUES.HUB_PORT,
        networkName: DEFAULT_VALUES.NETWORK_NAME,
        roomMembership: DEFAULT_VALUES.ROOM_MEMBERSHIP,
        groupsMembership: [],
        readings: readings,
        originalData: {
            ldsu_uuid: ldsuUuid,
            ldsu_name: ldsuName,
            SAID: sensorSAID,
            sensor_name: sensorName,
            sensor_type: sensorType,
            type: sensorType,
            unit: deviceRes.APP ? getPrimaryUnit(deviceRes.APP[Number(deviceRes.SAID)]) : deviceInfo?.UNIT || "",
            format: deviceRes.APP
                ? getPrimaryUnitName(deviceRes.APP[Number(deviceRes.SAID)])?.FORMAT
                : deviceInfo?.FORMAT,
            accuracy: deviceInfo?.ACCURACY || 2,
            readings: readings,
            cls: deviceInfo?.CLS,
            app_id: get(deviceRes.APP, `${Number(deviceRes.SAID)}.APPID`, ""),
            primaryUnitKey,
        },
    };
}

function sensorMountResolveSensorFormat(
    deviceRes: any,
    deviceInfo: any,
    sensorSAID: number
): string | undefined {
    return deviceRes.APP
        ? getPrimaryUnitName(deviceRes.APP[Number(sensorSAID)])?.FORMAT
        : deviceInfo?.FORMAT;
}

async function sensorMountFetchAndConfigureAlerts(
    ctx: SensorDetailsMountContext,
    gatewayId: string,
    ldsuUuid: string,
    sensorSAID: number,
    deviceRes: any,
    deviceInfo: any,
): Promise<void> {
    try {
        await sensorMountLoadSensorAlertConfiguration(
            ctx,
            gatewayId,
            ldsuUuid,
            sensorSAID,
            () =>
                sensorMountSetDefaultAlertMode(
                    ctx,
                    sensorMountResolveSensorFormat(deviceRes, deviceInfo, sensorSAID),
                    deviceInfo?.CLS
                ),
        );
    } catch {
        sensorMountSetDefaultAlertMode(
            ctx,
            sensorMountResolveSensorFormat(deviceRes, deviceInfo, sensorSAID),
            deviceInfo?.CLS
        );
    }
}

async function sensorMountFetchSensorFromApi(ctx: SensorDetailsMountContext): Promise<void> {
    if (!ctx.id || !ctx.sensorId) {
        ctx.setSensor(null);
        ctx.setIsPageLoading(false);
        return;
    }

    try {
        const parsed = sensorMountParseSensorIdFromString(ctx.sensorId);
        if (!parsed) {
            ctx.setSensor(null);
            ctx.setIsPageLoading(false);
            return;
        }
        const { ldsuUuid, sensorSAID } = parsed;

        const deviceRes = await fetchDetailDevice({
            gateway_id: ctx.id,
            device_id: ldsuUuid,
            said: sensorSAID.toString(),
        });

        if (!sensorMountValidateDeviceResponseData(deviceRes)) {
            ctx.setSensor(null);
            ctx.setIsPageLoading(false);
            return;
        }

        const { deviceInfo, primaryUnitKey } = sensorMountExtractDeviceInfoFromResponse(deviceRes, sensorSAID);
        const { sensorName, sensorType, ldsuName } = sensorMountExtractSensorMetadataFromResponse(
            deviceRes,
            sensorSAID,
            ctx.sensorId,
        );
        const readings = sensorMountExtractReadingsFromResponse(deviceRes);
        const apiSensor = sensorMountBuildSensorObjectFromData(deviceRes, deviceInfo, {
            ldsuUuid,
            sensorSAID,
            sensorName,
            sensorType,
            ldsuName,
            readings,
            primaryUnitKey,
        });

        ctx.setSensor(apiSensor);
        ctx.setSensorName(sensorName);
        ctx.setSensorJsonInfo(deviceInfo);

        if (deviceRes.MODE !== undefined) {
            ctx.setOpMode(Number(deviceRes.MODE));
        }

        if (ldsuUuid && ctx.id) {
            void ctx.fetchSensorMembership(ldsuUuid, ctx.id, sensorSAID);
        }

        await sensorMountFetchAndConfigureAlerts(ctx, ctx.id, ldsuUuid, sensorSAID, deviceRes, deviceInfo);
    } catch {
        ctx.setSensor(null);
        ctx.setIsPageLoading(false);
    }
}

function sensorMountFromNavDetails(
    ctx: SensorDetailsMountContext,
    sensorDetails: any,
    gatewayName: string | undefined,
    portNumber: string | undefined,
): void {
    const sensorName =
        sensorDetails.sensor_name ||
        sensorDetails.name ||
        sensorDetails.sensorName ||
        ctx.sensorId ||
        "Unknown Sensor";

    const sensorType = sensorDetails.sensor_type || sensorDetails.type || "Unknown Type";

    const ldsuName =
        sensorDetails.ldsu_name || sensorDetails.ldsuName || sensorDetails.name || "LDSU Device";

    const ldsuUuid =
        sensorDetails.ldsu_uuid || sensorDetails.ldsuUuid || sensorDetails.UID || "Unknown";

    let sensorSAID = 0;
    if (sensorDetails.SAID !== undefined) {
        sensorSAID = sensorDetails.SAID;
    } else if (ctx.sensorId?.includes("-")) {
        const parts = ctx.sensorId.split("-");
        const saidPart = parts.at(-1) ?? "";
        sensorSAID = Number.parseInt(saidPart, 10) || 0;
    }

    const realSensor = {
        name: sensorName,
        type: sensorType,
        sensor_type: sensorType,
        currentValue: sensorDetails.readings?.value || 0,
        minValue: sensorDetails.readings?.lowest || 0,
        maxValue: sensorDetails.readings?.highest || 0,
        unit: sensorDetails.unit || "",
        accuracy: sensorDetails.accuracy || 2,
        format: sensorDetails.format,
        lastActive: sensorDetails.readings?.timestamp
            ? moment(sensorDetails.readings.timestamp, "X").calendar(getCalendarFormat())
            : "-",
        ldsuName: ldsuName,
        ldsuUuid: ldsuUuid,
        SAID: sensorSAID,
        hubName: gatewayName || "-",
        hubPort: portNumber || DEFAULT_VALUES.HUB_PORT,
        networkName: DEFAULT_VALUES.NETWORK_NAME,
        roomMembership: DEFAULT_VALUES.ROOM_MEMBERSHIP,
        groupsMembership: [],
        readings: sensorDetails.readings || {
            value: 0,
            lowest: 0,
            highest: 0,
            timestamp: Date.now(),
        },
        originalData: sensorDetails,
    };

    ctx.setSensor(realSensor);
    ctx.setSensorName(sensorName);

    const membershipSaidForTags =
        sensorDetails.SAID !== undefined || (typeof ctx.sensorId === "string" && ctx.sensorId.includes("-"))
            ? sensorSAID
            : undefined;

    if (ldsuUuid && ctx.id) {
        void ctx.fetchSensorMembership(ldsuUuid, ctx.id, membershipSaidForTags);
    }

    void sensorMountFetchSensorConfigurationFromNav(ctx, sensorDetails, ldsuUuid, sensorSAID);
}

async function sensorMountFetchOrgMembers(ctx: SensorDetailsMountContext): Promise<void> {
    try {
        const { status, data = [] } = await OrganisationService.getOrgMembers(orgId());
        if (isHttpSuccess(status)) {
            const members: MemberDto[] = data.toCamelCase<MemberDto>().filter(
                (acc: MemberDto) => acc.status !== "Pending" && acc.status !== "Declined",
            );
            ctx.setInitialMemberList(members);
        }
    } catch {
        // Error fetching organization members - non-critical, continue without members list
    }
}

/**
 * Initial load: navigation state vs API fallback, device/alert config, org members list.
 * Extracted from useEffect so the effect callback stays within cognitive complexity limits.
 */
export function runSensorDetailsMountEffect(ctx: SensorDetailsMountContext): void {
    if (!ctx.validateAndRedirect()) {
        return;
    }

    const state = ctx.locationState;
    const sensorDetails = state?.sensorDetails;
    const gatewayName = state?.gatewayName;
    const portNumber = state?.portNumber;

    if (sensorDetails) {
        sensorMountFromNavDetails(ctx, sensorDetails, gatewayName, portNumber);
    } else {
        void sensorMountFetchSensorFromApi(ctx);
    }

    void sensorMountFetchOrgMembers(ctx);
    ctx.setIsPageLoading(false);
}
